import { useEffect, useRef, useState } from "react"
import { Routes, Route, useLocation, useNavigate } from "react-router-dom"
import { Loader } from "../Utilities/Loader"
import { Layout } from "../Utilities/Layout"
import { goToMobileVerification, goToRouteStep } from "../../Helpers/Routes"

import {
  LoanParameters,
  MobileVerification,
  PinVerification,
  PersonalDetails,
  ResidentialDetails,
  EmploymentDetails,
  IncomeDetails,
  ReferenceDetails,
  BankingDetails,
  LoanPreapproved,
  ReviewInProgress,
  PaymentVerification,
  IdentityVerification,
  IdentityVerificationError,
  IdentityVerificationSDK,
  IdentityVerificationSuccess,
} from "../Steps"

import { InstantLoanContext, useInstantLoanReducer } from "../../Helpers/Reducers/InstantLoan"
import { RequestsContext, useRequestsReducer } from "../../Helpers/Reducers/Requests"
import { UserContext, useUserReducer } from "../../Helpers/Reducers/User"
import { useMessagesReducer, MessagesContext } from "../../Helpers/Reducers/MessagesData"
import { ReviewDetails } from "../FastTrackSteps"
import { RepaymentOptions } from "../SidePanels/RepaymentOptions"
import {
  getUserReloadToken,
  getLoanStatus,
  getUser,
  getLoan,
  getGeneralProvinces,
  getStatusFastTrack,
  getMarketingConsent,
  getApplicationReviewMessages,
  getMostPopularInstitutions,
  getLatestIdentityVerification,
} from "../../Helpers/Requests"
import { findProvinceCodeById } from "../../Helpers/Parsers/Steps/LoanParameters/loanParameters"
import { postMessageSender } from "../../Helpers/PostMessage"
import { useStackDriver } from "../../Helpers/StackDriver"
import { LoanConnectContentType } from "../../Helpers/Cookies/loan-connect"
import { useLogRocket } from "../../Helpers/LogRocket"
import { getAggregators } from "../Steps/BankingDetails/helpers"

import Image2 from "../../images/support.png"
import Image3 from "../../images/symbol-instant-loans-half-left.svg"
import { InstitutionsContext, useInstitutionsReducer } from "../../Helpers/Reducers/Institutions"
import { BunkingIntroduction } from "../Steps/BankingDetails/components/Introduction"
import BankingSearch from "../Steps/BankingDetails/components/BankingSearch"
import BankAggregator from "../Steps/BankingDetails/components/BankAggregator"

const preloadSrcList: string[] = [Image2, Image3]

function preloadImage(src: string) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = function () {
      resolve(img)
    }
    img.onerror = img.onabort = function () {
      reject(src)
    }
    img.src = src
  })
}
function App() {
  const navigate = useNavigate()
  const [loanState, loanDispatch] = useInstantLoanReducer()
  const [requestsState, requestsDispatch] = useRequestsReducer()
  const [userState, userDispatch] = useUserReducer()
  const [messagesState, messagesDispatch] = useMessagesReducer()
  const [institutionsState, institutionsDispatch] = useInstitutionsReducer()
  const [isLoading, setLoading] = useState<boolean>(true)
  const { updateUser } = useStackDriver()
  const marketingReferralContent = useRef<LoanConnectContentType | null>(null)
  const tokenRef = useRef<string>("")
  const logRocket = useLogRocket()

  const { pathname, search } = useLocation()
  const path = pathname.substring(1)

  useEffect(() => {
    const searchParams = new URLSearchParams(search)
    const isApp = searchParams.get("isApp")
    if (isApp && isApp === "true") {
      loanDispatch({ step: "isApp", payload: { isApp: true } })
    }
  }, [search])

  // load token & write into state
  useEffect(() => {
    logRocket.init()

    const authEventListener = async (e: MessageEvent) => {
      let message: any | undefined = undefined
      try {
        message = typeof e.data === "string" ? JSON.parse(e.data) : e.data
      } catch (error) {
        console.warn(error)
        return
      }
      if (message.marketingReferralContent) {
        marketingReferralContent.current = message.marketingReferralContent
      }
      if (message.token) {
        tokenRef.current = message.token
        requestsDispatch({
          type: "setToken",
          payload: { token: message.token },
        })
      }
      if (message.route) {
        navigate(message.route)
      }

      if (message?.event === "camera_denied") {
        navigate("/identity-verification/error", {
          state: {
            title: "Identity verification",
            status: "camera_access",
          },
        })
      }

    }
    window.addEventListener("message", authEventListener)

    return () => {
      window.removeEventListener("message", authEventListener)
    }
  }, [])

  const onGetUserReloadToken = async () => {
    try {
      setLoading(true)
      const response = await getUserReloadToken(requestsState.token)
    } catch (error) {
      console.warn(error)
    }
  }

  // Here we reset the token every time minutes
  useEffect(() => {
    if (requestsState.token !== undefined && requestsState.token !== "") {
      setInterval(async () => onGetUserReloadToken, 600000) // 10 minutes = 600000
    }
  }, [requestsState])

  // check if application exists before navigating
  useEffect(() => {
    if (!requestsState.token) return

    const imagesPromiseList: Promise<any>[] = []
    for (const i of preloadSrcList) {
      imagesPromiseList.push(preloadImage(i))
    }

    Promise.all([
      getGeneralProvinces(),
      getLoanStatus(requestsState.token),
      getUser(requestsState.token),
      getLoan(requestsState.token),
      getStatusFastTrack(requestsState.token),
      getAggregators(requestsState.token),
      getMarketingConsent(requestsState.token),
      getApplicationReviewMessages(),
      getMostPopularInstitutions(requestsState.token),
      getLatestIdentityVerification(requestsState.token),
      ...imagesPromiseList,
    ])
      .then(
        ([
          provinces,
          loan,
          user,
          loanInfo,
          fastTrackInfo,
          aggregationData,
          marketingConsentData,
          applicationReviewMessages,
          mostPopular10institutions,
          latestIdentityVerification
        ]) => {
          if((user.result.hasActiveLoan || user.result.hasActiveApplication) && user.result.applicationStatusId >= 31) {
            postMessageSender({ event: "return" })
            return;
          }

          if (applicationReviewMessages) {
            messagesDispatch({
              type: "setApplicationReviewMessages",
              payload: {
                applicationReviewMessages,
              },
            })
          }

          if (aggregationData.error) {
            console.error(aggregationData.error)
            throw new Error("Aggregation data not found.")
          }

          if (mostPopular10institutions.data) {
            institutionsDispatch({
              type: "setInstitutions",
              payload: {
                items: mostPopular10institutions.data,
              },
            })
          }

          loanDispatch({
            step: "aggregator",
            payload: {
              aggregator: aggregationData.result,
            },
          })

          if (marketingConsentData.data) {
            loanDispatch({
              step: "marketingConsent",
              payload: {
                marketingConsent: marketingConsentData.data.subscribedAll,
              },
            })
          }

          if (provinces.result) {
            const provincesData = provinces.result
            loanDispatch({
              step: "provinces",
              payload: provincesData,
            })
          } else {
            postMessageSender({
              event: "error",
              error: {
                message: "Cannot fetch provinces info",
                data: {},
                code: 400,
              },
            })
          }

          // provide user into context
          userDispatch({
            type: "setUser",
            payload: { user: user.result },
          })

          if (user.result.email) {
            updateUser(user.result.email)
          }

          // LoanConnect integration -> setting phone number with cookie marketingReferralContent
          if (marketingReferralContent.current) {
            loanDispatch({
              step: "mobileVerification",
              payload: {
                mobileNumber: marketingReferralContent.current.phone,
              },
            })
          }

          const provinceCode = findProvinceCodeById(user.result.provinceId)

          if (loanInfo.amount != null && loanInfo.amount !== 0) {
            loanDispatch({
              step: "loanParameters",
              payload: { ...loanInfo, province: provinceCode },
            })
          } else {
            // LoanConnect integration -> setting amount with cookie marketingReferralContent when nothing is set.
            if (marketingReferralContent.current) {
              let amount = Number(marketingReferralContent.current.amount)
              if (isNaN(amount) || amount > 1500) {
                amount = 500
              }
              loanDispatch({
                step: "loanParameters",
                payload: { ...loanState.loanParameters, amount: amount, province: provinceCode },
              })
            } else {
              loanDispatch({
                step: "loanParameters",
                payload: { ...loanState.loanParameters, province: provinceCode },
              })
            }
          }

          setLoading(false)
          if (loan?.status === "canApply") {
            return
          }

          loanDispatch({
            step: "loading",
            payload: { loading: false },
          })

          if (user.result.applicationStatusId === 20) {
            const verificationStatus = latestIdentityVerification.data?.status

            if (latestIdentityVerification.data?.workflowType) {
              loanDispatch({
                step: "workflowType",
                payload: { workflowType: latestIdentityVerification.data.workflowType },
              })
            }

            switch (verificationStatus) {
              case "approved":
              case "manual_review":
                return navigate("/identity-verification/success", { state: { title: "Loan in review" } })

              case "in_progress":
              case "processing_result":
              case "awaiting_decision":
                return navigate("/identity-verification/verify", {
                  state: { title: "Identity verification" }
                })

              case "abandoned":
                return navigate("/identity-verification/error", {
                  state: {
                    title: "Identity verification",
                    status: "verification_incomplete",
                  }
                })

              case "pending_retry":
                return navigate("/identity-verification/error", {
                  state: {
                    title: "Identity verification",
                    status: "try_again",
                    attemptNumber: latestIdentityVerification.data?.retryCount,
                    maxRetryAttempts: latestIdentityVerification.data?.maxRetryAttempts,
                  }
                })

              case "expired":
                return navigate("/identity-verification/error", {
                  state: {
                    title: "Identity verification",
                    status: "session_expired",
                  }
                })

              case "declined":
              case "withdrawn":
                // These cases should never happen - do nothing
                break

              case "pending":
                return navigate("/identity-verification", {
                  state: { title: "Identity verification" }
                })
              default:
                return navigate("/identity-verification", {
                  state: { title: "Identity verification" }
                })
            }
          } else if (user.result.applicationStatusId === 30) {
            return navigate("/identity-verification/success", { state: { title: "Loan in review" } })
          }

          // load navigation state if app status exists
          if (
            fastTrackInfo?.data?.isFastTrackAvailable &&
            (fastTrackInfo?.data?.fastTrackWanted ?? true) &&
            fastTrackInfo?.data?.fastTrackStep === 1 &&
            user?.result?.applicationStep === 6
          ) {
            if (user.result.shouldValidatePhone) {
              goToMobileVerification(navigate)
            } else {
              return navigate("/fast-track/review", { state: { title: "Review and complete" } })
            }
          } else if (user.result.applicationStep != null && user.result.applicationStep !== 0) {
            if (user.result.shouldValidatePhone) {
              goToMobileVerification(navigate)
            } else {
              const step = user.result.applicationStep;
              if (user.result.previousLoanTransactionNotFound){
                return navigate("/payment-verification")
              }

              // Steps 5 (financial), 6 (reference) were removed from the UI.
              // Backend steps 5/6 now go to banking-details (route step 4).
              // Backend step 7 = banking, also route step 5.
              if (step >= 7) {
                goToRouteStep(
                  navigate,
                  5
                )
              } else if (step === 3 || step === 4 || step === 5 || step === 6) {
                goToRouteStep(
                  navigate,
                  4
                )
              } else {
                goToRouteStep(
                  navigate,
                  step + 1
                )
              }
            }
          }
        }
      )
      .catch((e) => {
        console.error("app error:", e)
      })
  }, [requestsState.token])

  useEffect(() => {
    postMessageSender({ event: "ready" })
  }, [])

  return (
    <UserContext.Provider value={{ state: userState, dispatch: userDispatch }}>
      <InstantLoanContext.Provider value={{ state: loanState, dispatch: loanDispatch }}>
        <RequestsContext.Provider value={{ state: requestsState, dispatch: requestsDispatch }}>
          <MessagesContext.Provider value={{ state: messagesState, dispatch: messagesDispatch }}>
            <InstitutionsContext.Provider value={{ state: institutionsState, dispatch: institutionsDispatch }}>
              <Loader loading={isLoading} extraPadding>
                <Routes>
                  <Route path="repayment-options" element={<RepaymentOptions />} />
                  <Route path="" element={<Layout />}>
                    <Route path="mobile-verification/*" element={<MobileVerification />} />
                    <Route path="pin-verification/*" element={<PinVerification />} />
                    <Route path="personal-details/*" element={<PersonalDetails />} />
                    <Route path="residential-details/*" element={<ResidentialDetails />} />
                    <Route path="employment-details/*" element={<EmploymentDetails />} />
                    <Route path="income-details/*" element={<IncomeDetails />} />
                    <Route path="reference-details/*" element={<ReferenceDetails />} />
                    <Route path="identity-verification" element={<IdentityVerification />} />
                    <Route path="identity-verification/verify" element={<IdentityVerificationSDK />} />
                    <Route path="identity-verification/error" element={<IdentityVerificationError />} />
                    <Route path="identity-verification/success" element={<IdentityVerificationSuccess />} />
                    <Route path="banking-details/*" element={<BankingDetails />}>
                      <Route path="" element={<BunkingIntroduction />} />
                      <Route path="search" element={<BankingSearch />} />
                      <Route path=":aggregator/bank/:id" element={<BankAggregator />} />
                    </Route>
                    <Route path="loan-preapproved" element={<LoanPreapproved />} />
                    <Route path="fast-track/review" element={<ReviewDetails />} />
                    <Route path="review-in-progress" element={<ReviewInProgress />} />
                    <Route path="payment-verification" element={<PaymentVerification />} />
                    <Route index element={<LoanParameters />} />
                  </Route>
                </Routes>
              </Loader>
            </InstitutionsContext.Provider>
          </MessagesContext.Provider>
        </RequestsContext.Provider>
      </InstantLoanContext.Provider>
    </UserContext.Provider>
  )
}

export default App
